import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getClickCollectStores } from '../../../utils/API/Delivery/deliveryAPI';
import { formatDeliveryLocationAddress } from '../../../utils/checkoutHelpers';
import { Log } from '../../../utils/API/Logging/LoggingAPI';

/**
 * Resolves the click & collect store location used for warehouse-aware
 * inventory checks.
 *
 * No store picker UI exists yet, so the first location returned by the
 * `clickcollectstores` endpoint is treated as the active store.
 */
export const useClickAndCollectLocationStore = defineStore('clickAndCollectLocation', () => {
  // State
  const warehouseCode = ref<string | undefined>(undefined);
  const storeName = ref<string | undefined>(undefined);
  const address = ref<string | undefined>(undefined);
  const isLoaded = ref(false);

  // Getters
  // Whether a usable click & collect store was resolved — false for both an
  // empty result and a fetch error, since `isLoaded` is only ever set `true`
  // on the success path.
  const hasStore = computed(() => isLoaded.value);

  // Actions
  async function fetchStore(): Promise<void> {
    try {
      const stores = await getClickCollectStores();
      const store = stores?.[0];
      warehouseCode.value = store?.id;
      storeName.value = store?.name;
      address.value = store ? formatDeliveryLocationAddress(store) : undefined;
      // Only mark as loaded once a store has actually resolved — an empty
      // result should not permanently block retries on a later call.
      isLoaded.value = !!store;
    } catch (error) {
      warehouseCode.value = undefined;
      storeName.value = undefined;
      address.value = undefined;
      Log({
        LogLevel: 'Error',
        Message: `[clickAndCollectLocationStore] Failed to load click & collect stores: ${error instanceof Error ? error.message : String(error)}`,
      });
    }
  }

  return {
    // State
    warehouseCode,
    storeName,
    address,
    isLoaded,
    // Getters
    hasStore,
    // Actions
    fetchStore,
  };
});
